import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  type Timestamp,
} from 'firebase/firestore';
import type { AgenticTask, TaskStep } from '@jam/shared';
import { auth, db } from './firebase';
import { readActiveOrgId } from './active-org';
import { auditLog } from './audit';
import { authHeaders } from './auth-headers';
import { logger } from './logger';

const TASKS = collection(db, 'tasks');

interface TaskDoc {
  orgId: string;
  ownerUid: string;
  title: string;
  entityName?: string;
  status: AgenticTask['status'];
  steps: TaskStep[];
  finalDeliverable?: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

function fromDoc(id: string, raw: TaskDoc): AgenticTask {
  return {
    id,
    orgId: raw.orgId ?? '',
    ownerUid: raw.ownerUid,
    title: raw.title,
    entityName: raw.entityName,
    status: raw.status,
    steps: raw.steps ?? [],
    finalDeliverable: raw.finalDeliverable,
    createdAt: raw.createdAt?.toDate().toISOString() ?? new Date().toISOString(),
    updatedAt: raw.updatedAt?.toDate().toISOString() ?? new Date().toISOString(),
  };
}

const DOSSIER_TEMPLATE: TaskStep[] = [
  { label: 'Extract entities', status: 'pending' },
  { label: 'Retrieve facts', status: 'pending' },
  { label: 'Retrieve sources', status: 'pending' },
  { label: 'Synthesize dossier', status: 'pending' },
];

/**
 * Create a new agentic task in Firestore with the four-step dossier
 * template. Returns the freshly read shape so callers can immediately
 * mutate it (e.g. the demo runner).
 */
export async function createTask(
  title: string,
  entityName?: string,
): Promise<AgenticTask> {
  const user = auth.currentUser;
  if (!user) throw new Error('not_signed_in');
  const orgId = readActiveOrgId();
  if (!orgId) throw new Error('no_active_org');
  const ref = await addDoc(TASKS, {
    orgId,
    ownerUid: user.uid,
    title,
    ...(entityName ? { entityName } : {}),
    status: 'queued',
    steps: DOSSIER_TEMPLATE,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  const now = new Date().toISOString();
  return {
    id: ref.id,
    orgId,
    ownerUid: user.uid,
    title,
    entityName,
    status: 'queued',
    steps: DOSSIER_TEMPLATE,
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Subscribe to the current user's queued + running tasks within the active
 * org, ordered by `updatedAt` desc. Returns an unsubscribe function.
 *
 * NB: Firestore's `in` operator on `status` requires a composite index on
 * `(orgId asc, ownerUid asc, status asc, updatedAt desc)` — see
 * firestore.indexes.json. No active org → empty tray + no-op unsubscribe
 * so a multi-org analyst doesn't see tasks from every tenant at once.
 */
export function subscribeActiveTasks(
  uid: string,
  onNext: (tasks: AgenticTask[]) => void,
): () => void {
  const activeOrgId = readActiveOrgId();
  if (!activeOrgId) {
    onNext([]);
    return () => {};
  }
  const q = query(
    TASKS,
    where('orgId', '==', activeOrgId),
    where('ownerUid', '==', uid),
    where('status', 'in', ['queued', 'running']),
    orderBy('updatedAt', 'desc'),
  );
  return onSnapshot(
    q,
    (snap) => {
      onNext(snap.docs.map((d) => fromDoc(d.id, d.data() as TaskDoc)));
    },
    (err) => {
      logger.warn('subscribeActiveTasks errored', { error: err.message });
    },
  );
}

/**
 * Subscribe to recently-updated tasks for the active org regardless of
 * status — used by the tray to show recently-completed tasks for ~30s after
 * they finish. No active org → empty list + no-op unsubscribe.
 */
export function subscribeRecentTasks(
  uid: string,
  onNext: (tasks: AgenticTask[]) => void,
): () => void {
  const activeOrgId = readActiveOrgId();
  if (!activeOrgId) {
    onNext([]);
    return () => {};
  }
  const q = query(
    TASKS,
    where('orgId', '==', activeOrgId),
    where('ownerUid', '==', uid),
    orderBy('updatedAt', 'desc'),
  );
  return onSnapshot(
    q,
    (snap) => {
      onNext(snap.docs.map((d) => fromDoc(d.id, d.data() as TaskDoc)));
    },
    (err) => {
      logger.warn('subscribeRecentTasks errored', { error: err.message });
    },
  );
}

export async function dismissTask(id: string): Promise<void> {
  await deleteDoc(doc(TASKS, id));
}

/**
 * Real server-driven dossier task. Creates the Firestore task doc with the
 * standard four-step template, then asks the API to execute it. Progress
 * updates land via the existing `subscribeRecentTasks` snapshot listener —
 * this function returns as soon as the server accepts the run request.
 *
 * The fetch awaits the server's terminal response (the orchestrator runs
 * synchronously inside the Cloud Function), so callers should treat the
 * resolved promise as "task is done or failed" — but the UI doesn't need
 * to wait for it; the tray reflects step transitions in real time.
 */
export async function runDossierTask(entityName: string): Promise<AgenticTask> {
  const task = await createTask(`Building full dossier on ${entityName}`, entityName);
  void auditLog('task.started', { target: task.id, meta: { entityName } });
  // Fire and forget the server call — the snapshot listener drives the UI.
  // We still surface fetch errors via a console warning so dev sees them.
  void fetch(`/api/tasks/${task.id}/run`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify({ entityName }),
  }).then(async (res) => {
    if (!res.ok) {
      // Best effort: mark the task as failed client-side so the tray shows
      // a Retry button even if the server never wrote a failure status.
      const detail = await res.text().catch(() => '');
      await patchTask(task.id, { status: 'failed' }).catch(() => undefined);
      console.warn('runDossierTask: server returned', res.status, detail);
    }
  });
  return task;
}

/**
 * Reset a failed task so it can be retried: every `failed` step becomes
 * `pending` (clearing its detail), the task status flips back to `queued`,
 * then we POST `/api/tasks/:id/run` again with the original entity.
 */
export async function retryTask(task: AgenticTask): Promise<void> {
  const entityName = task.entityName;
  if (!entityName) throw new Error('task_missing_entity');
  const resetSteps: TaskStep[] = task.steps.map((s) =>
    s.status === 'failed' || s.status === 'running'
      ? { ...s, status: 'pending', detail: undefined }
      : s,
  );
  await patchTask(task.id, { status: 'queued', steps: resetSteps });
  void auditLog('task.retried', { target: task.id, meta: { entityName } });
  void fetch(`/api/tasks/${task.id}/run`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify({ entityName }),
  }).then(async (res) => {
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      await patchTask(task.id, { status: 'failed' }).catch(() => undefined);
      console.warn('retryTask: server returned', res.status, detail);
    }
  });
}

async function patchTask(
  id: string,
  patch: Partial<Omit<TaskDoc, 'createdAt'>>,
): Promise<void> {
  await updateDoc(doc(TASKS, id), { ...patch, updatedAt: serverTimestamp() });
}

function delay(ms: number): Promise<void> {
  return new Promise((res) => setTimeout(res, ms));
}

/**
 * Client-side simulator (no server work) used to demo the task tray. Each
 * step transitions pending → running (300ms) → done (800ms) sequentially,
 * persisting every transition to Firestore so the tray re-renders live via
 * its `onSnapshot` subscription.
 *
 * The final "Synthesize dossier" step sets `finalDeliverable` to
 * `/entity/{name}`, which the tray renders as an "Open deliverable" link.
 */
export async function runDemoTask(entityName: string): Promise<AgenticTask> {
  const task = await createTask(`Building full dossier on ${entityName}`, entityName);
  const steps: TaskStep[] = task.steps.map((s) => ({ ...s }));

  await patchTask(task.id, { status: 'running' });

  for (let i = 0; i < steps.length; i += 1) {
    const current = steps[i];
    if (!current) continue;
    steps[i] = {
      ...current,
      status: 'running',
      detail: `Working on step ${i + 1} of ${steps.length}…`,
    };
    await patchTask(task.id, { steps: [...steps] });
    await delay(300);

    const result = describeResult(current.label, entityName);
    steps[i] = { ...current, status: 'done', detail: undefined, result };
    const isFinal = i === steps.length - 1;
    await patchTask(task.id, {
      steps: [...steps],
      ...(isFinal
        ? {
            status: 'done',
            finalDeliverable: `/entity/${encodeURIComponent(entityName)}`,
          }
        : {}),
    });
    await delay(800);
  }

  return { ...task, steps, status: 'done' };
}

function describeResult(label: string, entityName: string): string {
  switch (label) {
    case 'Extract entities':
      return `Anchored on ${entityName}`;
    case 'Retrieve facts':
      return 'Pulled facts from graph';
    case 'Retrieve sources':
      return 'Bundled source excerpts';
    case 'Synthesize dossier':
      return 'Dossier ready';
    default:
      return 'Done';
  }
}
